import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  Paper,
  IconButton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Search as SearchIcon, OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchAssetsByNamespace } from "../../../redux/actions/assetActions";

interface AssetPickerProps {
  namespace?: string;
  selectedNamespaceAssetRegistry: { namespace_assets: Record<string, string[]>; } | null;
  onSelectionChange: (namespace_asset_registry: { namespace_assets: Record<string, string[]>; } | null) => void;
  showToggle?: boolean;
}

const AssetPicker: React.FC<AssetPickerProps> = ({
  namespace: namespaceProp,
  selectedNamespaceAssetRegistry,
  onSelectionChange,
  showToggle = true,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    items: assets,
    loading,
    error,
  } = useSelector((state: RootState) => state.assets);

  const [currentNamespace, setCurrentNamespace] = useState(namespaceProp || "");
  const [searchTerm, setSearchTerm] = useState("");
  const [assetsEnabled, setAssetsEnabled] = useState(showToggle ? selectedNamespaceAssetRegistry !== null : true);
  
  // Get current namespace's selected asset IDs
  const currentNamespaceAssetIds = selectedNamespaceAssetRegistry?.namespace_assets && currentNamespace ? selectedNamespaceAssetRegistry.namespace_assets[currentNamespace] || [] : [];

  useEffect(() => {
    if (currentNamespace && assetsEnabled) {
      dispatch(fetchAssetsByNamespace(currentNamespace));
    }
  }, [dispatch, currentNamespace, assetsEnabled]);

  // Sync namespace state with namespace prop and trigger initial load
  useEffect(() => {
    if (namespaceProp !== undefined) {
      setCurrentNamespace(namespaceProp);
    }
  }, [namespaceProp]);

  // Sync assetsEnabled state with selectedNamespaceAssetRegistry prop
  useEffect(() => {
    setAssetsEnabled(showToggle ? selectedNamespaceAssetRegistry !== null : true);
  }, [selectedNamespaceAssetRegistry, showToggle]);

  const handleToggleEnabled = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked;
    setAssetsEnabled(enabled);
    if (enabled) {
      // If enabling, start with empty registry or preserve existing
      onSelectionChange(selectedNamespaceAssetRegistry || { namespace_assets: {} });
    } else {
      // If disabling, set to null
      onSelectionChange(null);
    }
  };

  const handleToggle = (assetId: string) => {
    if (selectedNamespaceAssetRegistry?.namespace_assets === null || !currentNamespace) return;
    
    const currentRegistry = { ...selectedNamespaceAssetRegistry?.namespace_assets };
    const currentNamespaceIds = currentRegistry[currentNamespace] || [];
    const currentIndex = currentNamespaceIds.indexOf(assetId);
    
    if (currentIndex === -1) {
      // Add asset to namespace
      currentRegistry[currentNamespace] = [...currentNamespaceIds, assetId];
    } else {
      // Remove asset from namespace
      const newNamespaceIds = [...currentNamespaceIds];
      newNamespaceIds.splice(currentIndex, 1);
      
      if (newNamespaceIds.length === 0) {
        // Remove namespace if no assets left
        delete currentRegistry[currentNamespace];
      } else {
        currentRegistry[currentNamespace] = newNamespaceIds;
      }
    }

    onSelectionChange({ namespace_assets: currentRegistry });
  };

  const filteredAssets = React.useMemo(() => {
    
    if (!searchTerm) return assets;
    
    const searchLower = searchTerm.toLowerCase();
    const filtered = assets.filter((asset) => {
      const matchesId = asset.id.toLowerCase().includes(searchLower);
      const matchesContent = asset.contents.some((content) => {
        if (content.type === "story") {
          return (
            content.subject?.toLowerCase().includes(searchLower) ||
            (content.story_items?.some((item) =>
              item.text.toLowerCase().includes(searchLower)
            ) ?? false)
          );
        } else if (content.type === "faq_entry") {
          return (
            content.faq_topic?.toLowerCase().includes(searchLower) ||
            content.faq_entry?.toLowerCase().includes(searchLower)
          );
        }
        return false;
      });
      
      return matchesId || matchesContent;
    });
    
    return filtered;
  }, [assets, searchTerm]);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {showToggle && (
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={assetsEnabled}
                onChange={handleToggleEnabled}
                name="enableAssets"
              />
            }
            label="Configure assets for this link"
          />
        </Box>
      )}

      {assetsEnabled && (
        <>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Namespace"
              value={currentNamespace}
              onChange={(e) => setCurrentNamespace(e.target.value)}
              placeholder="Enter namespace to load assets from"
              helperText="Enter the namespace from which you want to load assets"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              placeholder="Search assets..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error" align="center">
              {error}
            </Typography>
          ) : filteredAssets.length === 0 ? (
            <Box textAlign="center" py={3}>
              <Typography color="text.secondary">
                No assets found in namespace "{currentNamespace}"
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Assets will be filtered to only show items from this namespace
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Showing assets from namespace: {currentNamespace}
              </Typography>
              <List sx={{ maxHeight: 300, overflow: "auto", border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {filteredAssets.map((asset) => {
                  const isSelected = currentNamespaceAssetIds.includes(asset.id);
                  return (
                    <ListItem key={asset.id} disablePadding>
                      <ListItemButton onClick={() => handleToggle(asset.id)} dense>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          tabIndex={-1}
                          disableRipple
                        />
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2">{asset.id}</Typography>
                              <Chip
                                label={asset.type}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              {asset.deleted && (
                                <Chip
                                  label="Deleted"
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                />
                              )}
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`/dashboard/assets/${currentNamespace}/${asset.id}`, '_blank');
                                }}
                                title="View asset details"
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          }
                          secondary={
                            <Box>
                              {asset.contents.slice(0, 2).map((content, idx) => (
                                <Typography
                                  key={idx}
                                  variant="caption"
                                  color="text.secondary"
                                  component="div"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '500px'
                                  }}
                                >
                                  {content.type === "story" && content.subject
                                    ? `Story: ${content.subject.substring(0, 50)}${content.subject.length > 50 ? '...' : ''}`
                                    : content.type === "faq_entry" &&
                                      content.faq_topic
                                    ? `FAQ: ${content.faq_topic.substring(0, 50)}${content.faq_topic.length > 50 ? '...' : ''}`
                                    : `${content.type}`}
                                </Typography>
                              ))}
                              {asset.contents.length > 2 && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  component="div"
                                  fontStyle="italic"
                                >
                                  +{asset.contents.length - 2} more items
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}

          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              {selectedNamespaceAssetRegistry?.namespace_assets === null 
                ? "Assets not configured" 
                : currentNamespaceAssetIds.length === 0 
                ? "No assets selected in this namespace (explicitly empty)"
                : `${currentNamespaceAssetIds.length} asset${currentNamespaceAssetIds.length !== 1 ? "s" : ""} selected in this namespace`}
            </Typography>
          </Box>
        </>
      )}

      {!assetsEnabled && showToggle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Assets are not configured for this link
        </Typography>
      )}
    </Paper>
  );
};

export default AssetPicker;
